import { addDays, format, parseISO } from 'date-fns'
import { formatInTimeZone, fromZonedTime } from 'date-fns-tz'
import type { SnowflakeId } from '@/types/snowflake'
import i18n from '@/i18n'
import { intlLocale } from '@/i18n/dateFormat'

export interface UserRef {
  id: string
  fullName: string
  avatarUrl?: string
  email?: string
}

export interface Shift {
  id: string
  tenantId: string
  userId: string
  user?: UserRef
  scheduleDate: string
  shiftTypeCode?: string
  startAt: string
  endAt: string
  status: 'scheduled' | 'completed' | 'cancelled'
  notes?: string
  /** Resorts this shift covers (pms.group.id[]). NULL/empty = All resorts (unlimited). */
  groupIds?: SnowflakeId[] | null
  /** Task types this shift can take. Values are subtype codes (dotted, e.g.
   *  `maintenance.hvac`) or bare type codes (e.g. `maintenance`, = whole category).
   *  NULL/empty = All task types (unlimited). */
  taskSubtypeCodes?: string[] | null
  creator?: UserRef
  updater?: UserRef
  createdAt: string
  updatedAt: string
  // snake_case variants from API
  tenant_id?: string
  user_id?: string
  schedule_date?: string
  shift_type_code?: string
  start_at?: string
  end_at?: string
  created_at?: string
  updated_at?: string
}

export interface ShiftCreateRequest {
  user_id: string
  shift_type_code?: string
  /** 'YYYY-MM-DD' — required when start_at is omitted (off/rest-day shifts).
   *  Backend derives schedule_date from start_at for timed shifts, but for off
   *  shifts (no start_at) it must be supplied explicitly or the insert 500s. */
  schedule_date?: string
  start_at?: string  // optional: NULL/omitted for off-type shifts
  end_at?: string    // optional: NULL/omitted for off-type shifts
  status?: string
  /** Omit = unchanged (backend partial update); null = clear. */
  notes?: string | null
  /** Resort ids covered (pms.group.id[]). Omit = unchanged; null/[] = clear (unlimited). */
  group_ids?: string[] | null
  /** Task types covered (subtype dotted codes and/or bare type codes).
   *  Omit = unchanged; null/[] = clear (unlimited). */
  task_subtype_codes?: string[] | null
}

export interface ShiftBatchCreateItem {
  user_id: string
  shift_type_code?: string
  /** 'YYYY-MM-DD' — required when start_at is omitted (off/rest-day shifts) */
  schedule_date?: string
  start_at?: string
  end_at?: string
  status?: string
  notes?: string
  /** Resort ids covered (pms.group.id[]); empty/omitted = unlimited. */
  group_ids?: string[]
  /** Task types covered (subtype dotted codes and/or bare type codes); empty/omitted = unlimited. */
  task_subtype_codes?: string[]
}

export interface ShiftBatchResult {
  created_items: Shift[]
  /** rows overwritten in place when on_conflict=replace */
  replaced_items: Shift[]
  skipped_items: Array<{ reason: string; [key: string]: any }>
}

/**
 * Get the UTC offset string (e.g. "-05:00", "+08:00") for a given IANA timezone
 * on a specific date, sampled at 12:00 UTC of that date. On a DST-change day the
 * early hours (before the switch) get the post-switch offset — for a wall-clock
 * time use buildISOWithTimezone, which takes the offset at that exact time.
 */
export function getTimezoneOffsetString(dateStr: string, timezone: string): string {
  const date = new Date(`${dateStr}T12:00:00Z`)
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: timezone,
    timeZoneName: 'longOffset',
  }).formatToParts(date)
  const tzName = parts.find(p => p.type === 'timeZoneName')?.value || 'GMT'
  // tzName is like "GMT-05:00" or "GMT+05:30" or "GMT"
  const offset = tzName.replace('GMT', '')
  return offset || '+00:00'
}

/**
 * Build an ISO 8601 datetime string with the correct timezone offset.
 * e.g. buildISOWithTimezone("2026-03-15", "09:00", "America/New_York") => "2026-03-15T09:00:00-04:00"
 *
 * The offset is the one in force at that wall-clock time (date-fns-tz), not at noon of the
 * date: on a DST-change day 00:30 / 01:00 are still on the old offset (sampling noon put
 * those shifts off by an hour). A time inside the spring-forward gap still gets a valid
 * instant; an ambiguous fall-back time (01:30 happens twice) resolves the way date-fns-tz does.
 * Uses 'xxx' so UTC stays "+00:00", never "Z".
 */
export function buildISOWithTimezone(dateStr: string, timeStr: string, timezone: string): string {
  const instant = fromZonedTime(`${dateStr}T${timeStr}:00`, timezone)
  // 拼不出合法时刻(时间串不是 HH:mm)时退回按日取偏移,保持旧行为、不抛 RangeError
  const offset = Number.isNaN(instant.getTime())
    ? getTimezoneOffsetString(dateStr, timezone)
    : formatInTimeZone(instant, timezone, 'xxx')
  return `${dateStr}T${timeStr}:00${offset}`
}

/**
 * Start and end at the same wall-clock time = a zero-length shift. The backend rejects it
 * (end must be strictly after start); dialogs use this to block submit with a hint.
 * Only end < start means "runs past midnight".
 */
export function isZeroLengthShift(startTime: string | undefined | null, endTime: string | undefined | null): boolean {
  return !!startTime && startTime === endTime
}

/**
 * Build a shift's start/end ISO pair from a calendar date plus wall-clock HH:mm times.
 *
 * An end time before the start time means the shift runs past midnight (15:00 → 00:00,
 * 17:00 → 04:00), so the end lands on the NEXT calendar day (a calendar day, not +24h — each
 * end gets its own offset). An end EQUAL to the start is not a 24h shift: it stays on the same
 * day (zero length) for the backend to reject; UIs block it first with isZeroLengthShift. Building both ends on the same date
 * stored ~2,000 prod schedules whose end was before their start (2026-10-19 audit) — the gantt
 * showed "09:30 - 06:30" and anything comparing now against [start, end) never saw those staff
 * on shift. Every UI that turns (date, start, end) into a schedule range must go through here.
 * The next day's own UTC offset is used, so a DST change overnight is still correct.
 */
export function buildShiftRangeISO(
  dateStr: string,
  startTime: string,
  endTime: string,
  timezone: string,
): { startAt: string; endAt: string } {
  const endDate = endTime < startTime ? format(addDays(parseISO(dateStr), 1), 'yyyy-MM-dd') : dateStr
  return {
    startAt: buildISOWithTimezone(dateStr, startTime, timezone),
    endAt: buildISOWithTimezone(endDate, endTime, timezone),
  }
}

/** ISO / legacy 时间串 → 交给 render 取墙钟时间；空值、legacy "HH:MM:SS"、非法 ISO 的兜底两个函数共用。 */
function shiftTimeOf(
  value: string | undefined | null,
  render: (d: Date) => string,
): string {
  if (!value) return ''
  // ISO 8601 datetime: extract local time portion
  if (value.includes('T')) {
    try {
      const d = new Date(value)
      if (isNaN(d.getTime())) return value.substring(0, 5)
      return render(d)
    } catch {
      return ''
    }
  }
  // Legacy HH:MM:SS or HH:MM
  return value.substring(0, 5)
}

/** hourCycle 'h23'：hour12:false 在部分 ICU 下是 h24，午夜会出 "24:05"。 */
const shiftTimeOptions = (timezone?: string): Intl.DateTimeFormatOptions => ({
  hour: '2-digit',
  minute: '2-digit',
  hourCycle: 'h23',
  ...(timezone ? { timeZone: timezone } : {}),
})

/**
 * 界面上显示的班次时间（24 小时制），按当前界面语言（i18n.language，可用 locale 覆盖）格式化。
 * Handles both "2026-03-15T09:30:00-05:00" and legacy "09:30:00" formats.
 * When timezone is provided, displays time in that timezone instead of local computer time.
 * 只用于显示；<input type="time"> 的 value 用 toShiftTimeInputValue（严格 HH:mm，与语言无关）。
 */
export function formatShiftTime(
  value: string | undefined | null,
  timezone?: string,
  locale: string = i18n.language,
): string {
  return shiftTimeOf(value, (d) =>
    new Intl.DateTimeFormat(intlLocale(locale), shiftTimeOptions(timezone)).format(d),
  )
}

/**
 * ISO / legacy 时间串 → <input type="time"> 的 value：严格 "HH:mm"，与界面语言无关
 * （按 formatToParts 取数字分量自己拼，不吃任何 locale 的分隔符/数字体系）。
 * timezone 规则同 formatShiftTime：传了按该时区，不传按浏览器本地时区。
 */
export function toShiftTimeInputValue(value: string | undefined | null, timezone?: string): string {
  return shiftTimeOf(value, (d) => {
    const parts = new Intl.DateTimeFormat('en-US', shiftTimeOptions(timezone)).formatToParts(d)
    const part = (type: 'hour' | 'minute') => parts.find((p) => p.type === type)?.value ?? '00'
    return `${part('hour')}:${part('minute')}`
  })
}

/**
 * Read startAt/endAt from a shift, supporting both camelCase and snake_case,
 * and also legacy startTime/endTime fields for backward compatibility.
 */
export function getShiftStartEnd(shift: any): { startAt: string; endAt: string } {
  const startAt = shift?.startAt || shift?.start_at || shift?.startTime || shift?.start_time || ''
  const endAt = shift?.endAt || shift?.end_at || shift?.endTime || shift?.end_time || ''
  return { startAt, endAt }
}

export const SHIFT_STATUS_OPTIONS = [
  { value: 'scheduled', label: 'Scheduled' },
  { value: 'completed', label: 'Completed' },
  { value: 'cancelled', label: 'Cancelled' },
]

export const SHIFT_STATUS_COLORS: Record<string, string> = {
  scheduled: 'bg-blue-100 dark:bg-blue-950/50 text-blue-800',
  completed: 'bg-green-100 dark:bg-green-950/50 text-green-800',
  cancelled: 'bg-muted text-muted-foreground',
}

export const SHIFT_TYPE_COLORS: Record<string, string> = {
  morning: 'bg-blue-500',
  afternoon: 'bg-orange-500',
  night: 'bg-[#3A4B76]', // 夜班深蓝，与 my-shift/shiftColors 一致
}
